import {
  getActivePlanAssignmentsQueryKey,
  getManagedPlansOptions,
  getManagedPlansQueryKey,
  getPlanAndAssignmentsQueryKey,
  getPlansSummaryQueryKey,
  getUsersSummaryReportQueryKey,
} from '@cfa/pathway-fe-shared/xp';

import { queryClient } from '@/services';

import { getBroadQueryKeyId } from './getBroadQueryKeyId';

import { invalidateReportsPlans } from '@/routes/Reports/hooks/useGetPlansSummary';

export const invalidatePlanReportQueries = async (planId: string) => {
  await Promise.all([
    invalidateReportsPlans(),
    queryClient.invalidateQueries({
      queryKey: [{ _id: 'getPlanReport', path: { planId } }],
      refetchType: 'all',
    }),
  ]);
};

export const invalidateUserSummaryReportQueries = async (
  userIds?: string[],
) => {
  if (userIds?.length) {
    await Promise.all(
      userIds.map(userId =>
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getUserSummaryReport', path: { userId } }],
          refetchType: 'all',
        }),
      ),
    );
    return;
  }

  await queryClient.invalidateQueries({
    queryKey: [{ _id: 'getUserSummaryReport' }],
    refetchType: 'all',
  });
};

export const invalidatePlanListQueries = async () => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: getPlansSummaryQueryKey(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: getActivePlanAssignmentsQueryKey(),
      refetchType: 'all',
    }),
  ]);
};

export const invalidateUsersSummaryReportQueries = () =>
  queryClient.invalidateQueries({
    queryKey: [{ _id: 'getUsersSummaryReport' }],
    refetchType: 'all',
  });

/**
 * Invalidates all plan-related queries that reflect assignment/completion status.
 * Call after any mutation that changes plan status (complete, reset, reassign).
 */
export const invalidateAllPlanQueries = async (planId?: string) => {
  // Remove inactive query caches so they're forced to refetch on next mount.
  // Needed because the global queryClient has refetchOnMount: false.
  const inactiveKeys = [getActivePlanAssignmentsQueryKey()];
  for (const queryKey of inactiveKeys) {
    queryClient.removeQueries({ queryKey });
  }

  const promises: Promise<void>[] = [
    queryClient.refetchQueries({
      queryKey: getActivePlanAssignmentsQueryKey(),
      type: 'all',
    }),
    queryClient.refetchQueries({
      queryKey: getManagedPlansQueryKey(),
      type: 'all',
    }),
  ];

  if (planId) {
    promises.push(
      queryClient.refetchQueries({
        queryKey: getPlanAndAssignmentsQueryKey({ path: { planId } }),
        type: 'all',
      }),
    );
  }

  await Promise.all(promises);
};

export const invalidateManagedPlansQueries = () =>
  queryClient.refetchQueries({
    queryKey: getManagedPlansQueryKey(),
    type: 'all',
  });

/**
 * Invalidates the lists affected by archiving/activating a plan: the Manage
 * Plans list (which is split by status) and the My Plans list (active plan
 * assignments). Call after any mutation that changes a plan's archived status.
 */
export const invalidatePlanArchiveStatusQueries = async () => {
  // Remove inactive My Plans cache so it refetches on next mount.
  // Needed because the global queryClient has refetchOnMount: false.
  queryClient.removeQueries({
    queryKey: getActivePlanAssignmentsQueryKey(),
    type: 'inactive',
  });

  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: getManagedPlansQueryKey(),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: getActivePlanAssignmentsQueryKey(),
      refetchType: 'all',
    }),
    queryClient.refetchQueries({
      queryKey: getManagedPlansOptions({ query: { status: 'ACTIVE' } })
        .queryKey,
    }),
    queryClient.invalidateQueries({
      queryKey: [getBroadQueryKeyId(getUsersSummaryReportQueryKey)],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [getBroadQueryKeyId(getPlanAndAssignmentsQueryKey)],
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({
      queryKey: [getBroadQueryKeyId(getPlansSummaryQueryKey)],
      refetchType: 'all',
    }),
  ]);
};

export const invalidatePlanAndAssignmentsQueries = (planId: string) =>
  queryClient.invalidateQueries({
    queryKey: getPlanAndAssignmentsQueryKey({ path: { planId } }),
    refetchType: 'all',
  });

export const invalidateCertificateQueries = async (
  planId: string,
  userIds?: string[],
) => {
  await Promise.all([
    invalidatePlanReportQueries(planId),
    invalidatePlanAndAssignmentsQueries(planId),
    invalidateManagedPlansQueries(),
    invalidateUserSummaryReportQueries(userIds),
    invalidatePlanListQueries(),
    invalidateUsersSummaryReportQueries(),
  ]);
};
